import type { QuizAnswer, QuizChapter } from "../types/Quiz";

type QuestionSource<TQuestion> = {
  chapters: QuizChapter<TQuestion>[];
};

export const QUIZ_CHAPTERS_INTEGRATION_ERROR =
  "Unable to load this Quiz: expected quiz.chapters. Check the backend migration.";

export const assertQuizChapters = (configuration: unknown): void => {
  if (!hasQuizChapters(configuration)) {
    throw new Error(QUIZ_CHAPTERS_INTEGRATION_ERROR);
  }
};

/** Structural contract check, not a replacement for backend authoring validation. */
export const hasQuizChapters = (
  configuration: unknown,
): configuration is QuestionSource<unknown> => {
  if (!configuration || typeof configuration !== "object") return false;
  const candidate = configuration as Record<string, unknown>;
  return (
    !("questions" in candidate) &&
    Array.isArray(candidate.chapters) &&
    candidate.chapters.every(
      (chapter: unknown) =>
        chapter !== null &&
        typeof chapter === "object" &&
        Array.isArray((chapter as Record<string, unknown>).questions),
    )
  );
};

/** A flat response signals an incomplete integration, never an empty quiz. */
export const getQuizQuestions = <TQuestion>(
  configuration: QuestionSource<TQuestion>,
): TQuestion[] => {
  if (!hasQuizChapters(configuration)) {
    throw new Error("Expected quiz.chapters with ordered question arrays.");
  }
  return configuration.chapters.flatMap((chapter) => chapter.questions);
};

export const getQuizQuestionCount = <TQuestion>(
  configuration: QuestionSource<TQuestion>,
): number => getQuizQuestions(configuration).length;

export const findQuizQuestionChapter = <TQuestion extends { _id?: string }>(
  chapters: QuizChapter<TQuestion>[],
  questionId: string,
): QuizChapter<TQuestion> | undefined =>
  chapters.find((chapter) =>
    chapter.questions.some((question) => question._id === questionId),
  );

/** Authoring only: preserve IDs, labels, and scoring without mutating read data. */
export const normalizeQuizChaptersForAuthoring = <
  TQuestion extends { answers: QuizAnswer[] },
>(
  chapters: QuizChapter<TQuestion>[],
): QuizChapter<TQuestion>[] =>
  chapters.map((chapter) => ({
    ...chapter,
    questions: chapter.questions.map((question) => ({
      ...question,
      answers: question.answers.map((answer) => ({
        ...answer,
        riskFlag: answer.riskFlag ?? false,
      })),
    })),
  }));
